import { createPrng } from '@play-co/replicant';
import {
  Match2Reward,
  Match2TimedRewardType,
  PredefinedRewardId,
  RandomRewardConfig,
  RewardSet,
  RewardWrapper,
  isRandomReward,
  isTimedReward,
  match2PredefinedRewards,
} from './match2-rewards.ruleset';
import {
  getRandomFloat,
  getRandomInt,
  getRandomItemFromArray,
} from '../../utils/random';
import { clamp } from '../../utils';
import { match2Boosters, match2Powerups } from '../match2.ruleset';
import { getJSTDateTime } from '../../utils/time';

function deepClone(obj: object) {
  return JSON.parse(JSON.stringify(obj));
}

export function getProcessedMatch2Rewards(
  id: PredefinedRewardId,
  timestamp: number,
): Match2Reward[] {
  const raw = getMatch2PredefinedReward(id);
  return processMatch2Rewards(raw, timestamp);
}

export function getMatch2PredefinedReward(
  id: PredefinedRewardId,
): Match2Reward[] {
  // Deep clone predefined reward to prevent accidental mutation
  return deepClone(match2PredefinedRewards[id]);
}

export function processMatch2Rewards(
  rewards: Match2Reward[],
  timestamp: number,
): Match2Reward[] {
  const out: Match2Reward[] = [];

  for (let index = 0; index < rewards.length; index++) {
    const reward = rewards[index];

    switch (reward.type) {
      case 'wrapper':
        out.push(...processRewardWrapper(reward, timestamp));
        break;
      case 'reward_set':
        out.push(...processRewardSet(reward, timestamp));
        break;
      default:
        out.push(processReward(reward, timestamp));
        break;
    }
  }

  return consolidateRewards(out);
}

/**
 * Flatten reward duplicates within a reward set
 * @param reward reward set
 * @returns flattened reward set
 */
export function consolidateRewards(rewards: Match2Reward[]): Match2Reward[] {
  const out: Match2Reward[] = [];

  rewards.forEach((reward) => {
    // Super Lollipop is not cumulative, only one will activate it
    // So if Super Lollipop already exists, skip adding another
    if (
      reward.type === 'feature' &&
      reward.subType === 'superLollipop' &&
      out.some((r) => r.type === 'feature' && r.subType === 'superLollipop')
    ) {
      return;
    }

    const existing = out.find((r) => {
      const typeMatched = r.type === reward.type;
      const subTypeMatched =
        ('subType' in r && r.subType) ===
        ('subType' in reward && reward.subType);
      const bothContainsAmount = 'amount' in r && 'amount' in reward;
      const bothContainsDuration = isTimedReward(r) && isTimedReward(reward);

      return (
        typeMatched &&
        subTypeMatched &&
        (bothContainsAmount || bothContainsDuration)
      );
    });

    if (existing) {
      if ('amount' in existing && 'amount' in reward)
        existing.amount! += reward.amount!;
      else if (isTimedReward(existing) && isTimedReward(reward))
        existing.duration += reward.duration;
    } else {
      // Deep-clone the rewards to avoid accidental mutation
      out.push(JSON.parse(JSON.stringify(reward)));
    }
  });

  return out;
}

// --------------------- NESTED REWARDS --------------------------

function processRewardWrapper(wrapper: RewardWrapper, timestamp: number) {
  // Deep clone reward wrapper to prevent accidental mutation
  wrapper = deepClone(wrapper);

  const out: Match2Reward[] = [];

  if ('ids' in wrapper) {
    wrapper.ids.forEach((id) => {
      out.push(...getMatch2PredefinedReward(id));
    });
  } else if ('id' in wrapper) {
    const reward = getMatch2PredefinedReward(wrapper.id);
    out.push(...new Array(wrapper.amount).fill(reward).flat());
  }

  return processMatch2Rewards(out, timestamp);
}

function processRewardSet(rewardSet: RewardSet, timestamp: number) {
  // Deep clone reward set to prevent accidental mutation
  rewardSet = deepClone(rewardSet);

  if (!isRandomReward(rewardSet))
    return processMatch2Rewards(rewardSet.rewards, timestamp);

  const out: Match2Reward[] = [];
  const random = createPrng(`${timestamp}`);
  const pool = rewardSet.pool;
  const amount = clamp(rewardSet.amount ?? 1, 1, pool.length);

  for (let index = 0; index < amount; index++) {
    const reward = getRandomItemFromArray(pool, random);
    out.push(processReward(reward, timestamp));

    if (rewardSet.noOverlap) {
      pool.splice(pool.indexOf(reward), 1);
    }
  }

  return processMatch2Rewards(out, timestamp);
}

// ------------------- RANDOMIZED REWARDS ------------------------

function processReward(reward: Match2Reward, timestamp: number): Match2Reward {
  if (!isRandomReward(reward)) return reward;
  const random = createPrng(`${timestamp}`);

  switch (reward.type) {
    case 'bPoints':
      return {
        type: reward.type,
        amount: getRandomizedValue(reward, random),
      };
    case 'gems':
      return {
        type: reward.type,
        amount: getRandomizedValue(reward, random),
      };
    case 'boosters': {
      const subType = getRandomItemFromArray([...match2Boosters], random);
      return 'amount' in reward
        ? { type: 'boosters', subType, amount: reward.amount }
        : { type: 'boosters', subType, duration: reward.duration };
    }
    case 'powerups': {
      const subType = getRandomItemFromArray([...match2Powerups], random);
      return 'amount' in reward
        ? { type: 'powerups', subType, amount: reward.amount }
        : { type: 'powerups', subType, duration: reward.duration };
    }
    default:
      return reward;
  }
}

function getRandomizedValue(
  { min = 0, max, step = 1, floor = false }: RandomRewardConfig,
  random: () => number,
) {
  let value = min;

  if (floor) {
    value = getRandomInt(min, max, random);
  } else {
    value = getRandomFloat(min, max, random);
  }

  return Math.round(value / step) * step;
}

// ----------------------------------------------------------------

export function getMatch2TimedRewardType(
  reward: Match2Reward,
): Match2TimedRewardType | null {
  if (!isTimedReward(reward) || isRandomReward(reward)) return null;
  if (reward.type === 'energy') return 'energy';
  return `${reward.type}-${reward.subType}` as Match2TimedRewardType;
}

export function getMatch2TimedRewardTimestampKey(
  type: Match2TimedRewardType,
): string {
  return `unlimited-${type}`;
}

/**
 * Returns the cooldown until the next daily bonus at midnight.
 * @param now
 * @returns
 */
export function getDailyBonusCooldownUntilMidnight(now: number): number {
  const nowTime = getJSTDateTime(now);
  const midnightTime = nowTime.plus({ days: 1 }).startOf('day');
  const cooldown = midnightTime.diff(nowTime, 'milliseconds').milliseconds;
  return cooldown;
}
